
import { computed, defineComponent, inject, nextTick, ref, watch } from "vue";
import { fieldSegment, fragmentSegment } from "./selectionModel";

const MAX_DEPTH = 10;

export default defineComponent({
  name: "SelectionSetTreeNode",
  props: {
    segments: { type: Array as () => string[], required: true },
    meta: { type: Object as () => any, required: true },
    depth: { type: Number, default: 0 },
    ancestorTypes: { type: Array as () => string[], default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  setup(props: any) {
    const tree: any = inject("selectionSetTree");
    const open = ref(false);
    const loading = ref(false);
    const loadError = ref(false);
    const checkbox = ref<HTMLInputElement | null>(null);

    // A field node expands into its type; a fragment (... on Type) node into its member type.
    const childTypeName = computed(() => props.meta.onType || props.meta.type);
    const isFragment = computed(() => props.meta.kind === "FRAGMENT");
    const label = computed(() =>
      isFragment.value ? `... on ${props.meta.onType}` : props.meta.name
    );
    const typeLabel = computed(() => {
      const { kind } = props.meta;
      if (kind === "UNION" || kind === "INTERFACE") {
        return `${props.meta.type} · ${kind.toLowerCase()}`;
      }
      return props.meta.type;
    });
    const rowId = computed(
      () => props.segments.join(".") || (props.meta.isRoot ? "all" : "")
    );
    const isRecursive = computed(() =>
      props.ancestorTypes.includes(childTypeName.value)
    );
    const depthExceeded = computed(() => props.depth >= MAX_DEPTH);

    const state = computed(() => tree.stateOf(props.segments, props.meta));
    // Deprecated fields can't be newly selected (they produce failing requests on some APIs), but a
    // value already carrying one — e.g. a step saved before this rule — can still be UNCHECKED so
    // the user can fix it. Required-arg fields stay fully disabled (see design section 10).
    const checkDisabled = computed(
      () =>
        props.meta.requiredArgs ||
        props.disabled ||
        (props.meta.deprecated && !state.value.checked)
    );
    const loaded = computed(() => !!tree.schemaFor(childTypeName.value));
    const children = computed(() =>
      loaded.value ? tree.childrenMeta(childTypeName.value) : []
    );

    // The indeterminate state of a checkbox is DOM-only, not a bindable attribute.
    watch(
      () => state.value.indeterminate,
      (value) => {
        if (checkbox.value) checkbox.value.indeterminate = value;
      },
      { immediate: true }
    );
    watch(checkbox, (el) => {
      if (el) el.indeterminate = state.value.indeterminate;
    });

    async function toggleOpen() {
      if (!props.meta.expandable) return;
      open.value = !open.value;
      // Recursive types are allowed to expand but only on this explicit click (never auto-loaded),
      // which keeps cyclic schemas finite. Depth-capped nodes just show a note, no fetch.
      if (open.value && !depthExceeded.value && !loaded.value) {
        loading.value = true;
        loadError.value = false;
        try {
          await tree.loadType(childTypeName.value);
        } catch (e) {
          // A rejected fetch (network/timeout) would otherwise leave the node expanded and empty
          // with no feedback; surface it so the user knows to retry by collapsing/re-expanding.
          loadError.value = true;
        } finally {
          loading.value = false;
        }
      }
      await nextTick();
      if (checkbox.value)
        checkbox.value.indeterminate = state.value.indeterminate;
    }

    function onCheck(event: Event) {
      const nativeChecked = (event.target as HTMLInputElement).checked;
      // The "(all)" root is a select-all / deselect-all control. It is rarely "fully checked" (that
      // needs every nested branch loaded and checked), so it usually sits indeterminate — and a
      // native tri-state click on an indeterminate box always reports checked=true, which could
      // never reach the empty state. Drive it from the logical selection instead: any existing
      // selection clears; an empty one selects all loaded scalars.
      const next = props.meta.isRoot
        ? !(state.value.checked || state.value.indeterminate)
        : nativeChecked;
      tree.toggle(props.segments, props.meta, next);
      // The computed `checked` for the root rarely changes value, so Vue won't reset the DOM
      // property the browser just flipped — sync both flags back to the logical state.
      if (props.meta.isRoot) {
        nextTick(() => {
          if (checkbox.value) {
            checkbox.value.checked = state.value.checked;
            checkbox.value.indeterminate = state.value.indeterminate;
          }
        });
      }
    }

    function onLabelClick() {
      const next = !state.value.checked;
      // Same rule as the checkbox: block selecting, allow clearing an already-set deprecated field.
      if (props.meta.requiredArgs || props.disabled) return;
      if (props.meta.deprecated && next) return;
      tree.toggle(props.segments, props.meta, next);
    }

    function segmentOf(child: any): string {
      return child.kind === "FRAGMENT"
        ? fragmentSegment(child.onType)
        : fieldSegment(child.name);
    }

    function childKey(child: any): string {
      return child.kind === "FRAGMENT" ? `frag:${child.onType}` : child.name;
    }

    return {
      open,
      loading,
      checkbox,
      childTypeName,
      label,
      typeLabel,
      rowId,
      isRecursive,
      depthExceeded,
      loadError,
      state,
      checkDisabled,
      loaded,
      children,
      toggleOpen,
      onCheck,
      onLabelClick,
      segmentOf,
      childKey,
    };
  },
});
